import { Injectable } from '@angular/core';
import { CtResult } from '@cloud-twin-v2/ct-map/models/ct-result';
import { ChartResultDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { AdditionalHttpOpts } from '@common-modules/cache/http-cache/additional-http-options';
import { BaseService } from '@common-modules/shared/base.service';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  ReplaySubject,
  tap,
} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CTResultsService extends BaseService {
  private readonly _results$ = new ReplaySubject<CtResult[]>(1);
  private readonly _suspendResults$ = new BehaviorSubject<boolean>(false);
  private readonly _timeWindow = 12 * 60 * 60 * 1000;
  private _startDateRange: number;
  private _endDateRange: number;

  readonly results$ = combineLatest([this._results$.asObservable(), this._suspendResults$]).pipe(
    map(([results, suspended]) => (suspended ? null : results)),
    filter((results) => results !== null),
  );

  setResultsSuspended(flag: boolean) {
    this._suspendResults$.next(flag);
  }

  load(scenarioIds: string[], startDate: Date, endDate: Date): Observable<any> {
    return this.getResultsByStep(scenarioIds, startDate, endDate).pipe(
      tap((metadata) => {
        this.updateResultsAndDates(metadata);
      }),
    );
  }

  update(scenarioIds: string[], timeStep: Date, startDate: Date, endDate: Date): Observable<any> {
    this.setResultsSuspended(true);

    if (this.checkTimeStepInCache(timeStep)) {
      this.setResultsSuspended(false);
      return of(true);
    }

    return this.getResultsByStep(scenarioIds, startDate, endDate).pipe(
      tap((metadata) => {
        this.updateResultsAndDates(metadata);
        this.setResultsSuspended(false);
      }),
    );
  }

  getChartResults(chartResultsDto: ChartResultDto[]): Observable<CtResult[]> {
    const content_ = JSON.stringify(chartResultsDto);
    return this.httpCacheClient.post<CtResult[]>(`${this.apiUrl}/result/chart`, chartResultsDto, {
      avoid: false,
    });
  }

  private updateResultsAndDates(metadata: CtResult[]) {
    this._results$.next(metadata);
    const startDates = metadata.map((r) => (new Date(r.startDate)).getTime());
    const endDates = metadata.map((r) => (new Date(r.endDate)).getTime());
    this._startDateRange = Math.min(...startDates);
    this._endDateRange = Math.max(...endDates);
  }

  private fetchResults(params: any, avoidCache: any): Observable<CtResult[]> {
    const requestDatesNoUtc = ['startDate', 'endDate', 'dateStr'];
    const additionalHttpOptions = new AdditionalHttpOpts({
      mapRequestDates: true,
      mapResponseDates: true,
      requestDatesNoUtc,
      //warnIfMissing: requestDatesNoUtc,
    });
    return this.httpCacheClient.get<CtResult[]>(
      `${this.apiUrl}/result/range`,
      avoidCache,
      params,
      additionalHttpOptions,
    );
  }

  private getResults(scenarioIds: string[]): Observable<CtResult[]> {
    return this.fetchResults({ scenarioIds }, { avoid: false });
  }

  private checkTimeStepInCache(timeStep: Date): boolean {
    const timeStepInms = (new Date(timeStep)).getTime();
    return this._startDateRange <= timeStepInms && timeStepInms <= this._endDateRange;
  }

  private getResultsByStep(scenarioIds: string[], startDate: Date, endDate: Date): Observable<CtResult[]> {
    const startRange = startDate.toISOString();
    const endRange = endDate.toISOString();
    return this.fetchResults({ scenarioIds, startRange, endRange }, { avoid: true });
  }
}
